import { Box, HStack, VStack } from "@chakra-ui/react";
import type { FieldErrors } from "react-hook-form";
import { type Ref } from "react";
import { GUIDED_EDIT_DEFAULT_TIMEZONE } from "@common/interfaces/adsManagerGuidedEdit";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import type { ScheduledBudgetAdDeliveryWindowUtc } from "../../utils/eventWallTime";
import type { BudgetData, BudgetPhaseForm } from "./BudgetSelector";
import { GuidedBudgetAmountSummary } from "./GuidedBudgetAmountSummary";
import { GuidedBudgetEditorHeader } from "./GuidedBudgetEditorHeader";
import { CampaignRunScheduleBlock } from "./CampaignRunScheduleBlock";
import { GuidedBudgetScheduledPhasesSection } from "./GuidedBudgetScheduledPhasesSection";
import { GuidedBudgetTypeSelect } from "./GuidedBudgetTypeSelect";
import { useGuidedBudgetScheduled } from "../../hooks/useGuidedBudgetScheduled";

export type GuidedBudgetMode = "daily" | "lifetime" | "scheduled";

export type GuidedBudgetEditorProps = {
  budget?: BudgetData;
  /** IANA zone for interpreting phase datetime-local values as wall time (same as campaign schedule). */
  eventTimezone?: string;
  /**
   * Full ad delivery window (campaign run start → end, UTC ISO). Phased budget times must
   * stay inside this window in the UI and on patch (same checks as save / publish).
   */
  scheduledBudgetDeliveryWindow?: ScheduledBudgetAdDeliveryWindowUtc | null;
  /** When false, only daily/lifetime (e.g. quick edit modal). */
  allowScheduled?: boolean;
  /**
   * Campaign is synced to Meta (`external_id`): budget type cannot change and ended scheduled
   * phases are read-only.
   */
  linkedToMeta?: boolean;
  onChangeBudgetType: (type: GuidedBudgetMode) => void;
  onChangeBudgetAmount: (amount: number | null) => void;
  onAddSegment?: () => void;
  onRemoveSegment?: (index: number) => void;
  onChangeSegment?: (index: number, patch: Partial<BudgetPhaseForm>) => void;
  /** When set (e.g. modal initial focus), forwards to the dollar amount input for simple budgets. */
  amountInputRef?: Ref<HTMLInputElement>;
  /**
   * When set (edit budget modal), Yup/RHF `errors` drive phased row copy and outlines.
   * Omit for create flow (local timeline validation).
   */
  scheduledBudgetFormErrors?: FieldErrors<BudgetData>;
  /**
   * When set, campaign run dates render in their own card **above** the budget card (always).
   * Omit when there is no run schedule to edit in this surface (e.g. budget-only modal).
   */
  campaignRunSchedule?: {
    startAt: string;
    endAt: string;
    onChangeStartAt: (value: string) => void;
    onChangeEndAt: (value: string) => void;
    /** When true, run dates are display-only (e.g. linked campaign with phased scheduled budget). */
    readOnly?: boolean;
    /** Shown below the schedule when {@link campaignRunSchedule.readOnly} is true. */
    readOnlyHelperText?: string;
  } | null;
};

dayjs.extend(utc);
dayjs.extend(timezone);

export default function GuidedBudgetEditor({
  budget,
  eventTimezone = GUIDED_EDIT_DEFAULT_TIMEZONE,
  scheduledBudgetDeliveryWindow = null,
  allowScheduled = true,
  linkedToMeta = false,
  onChangeBudgetType,
  onChangeBudgetAmount,
  onAddSegment,
  onRemoveSegment,
  onChangeSegment,
  amountInputRef,
  scheduledBudgetFormErrors,
  campaignRunSchedule = null,
}: GuidedBudgetEditorProps) {
  const mode: GuidedBudgetMode =
    budget?.type === "scheduled"
      ? "scheduled"
      : budget?.type === "daily"
        ? "daily"
        : "lifetime";

  const isScheduled = mode === "scheduled";

  const {
    scheduledSegments,
    scheduledBudgetEditPolicy,
    scheduledTotal,
    deliveryWindowInEventTimezone,
    scheduledPhaseTimelineError,
    phaseNeighborPickerBounds,
  } = useGuidedBudgetScheduled({
    isScheduled,
    budget,
    eventTimezone,
    scheduledBudgetDeliveryWindow,
    scheduledBudgetFormErrors,
    linkedToMeta,
  });

  const cardContainerProps = {
    p: 6,
    borderRadius: "2xl",
    border: "1px solid",
    borderColor: "#DDDDDD",
    bg: "white",
    w: "full",
    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.05)",
  } as const;

  return (
    <VStack align="stretch" spacing={4} w="full">
      {campaignRunSchedule ? (
        <Box {...cardContainerProps}>
          <CampaignRunScheduleBlock
            eventTimezone={eventTimezone}
            startAt={campaignRunSchedule.startAt}
            endAt={campaignRunSchedule.endAt}
            onChangeStartAt={campaignRunSchedule.onChangeStartAt}
            onChangeEndAt={campaignRunSchedule.onChangeEndAt}
            isScheduleRangeReadOnly={campaignRunSchedule.readOnly === true}
            scheduleReadOnlyHelperText={campaignRunSchedule.readOnlyHelperText}
            pickerOpenIconAtStart
          />
        </Box>
      ) : null}

      <Box {...cardContainerProps}>
        <VStack align="stretch" spacing={3} w="full">
          <GuidedBudgetEditorHeader />

          <HStack
            align="stretch"
            spacing={{ base: 3, md: 6 }}
            w="full"
            flexWrap={{ base: "wrap", md: "nowrap" }}
          >
            <GuidedBudgetTypeSelect
              mode={mode}
              allowScheduled={allowScheduled}
              linkedToMeta={linkedToMeta}
              onChangeBudgetType={onChangeBudgetType}
            />
            <GuidedBudgetAmountSummary
              isScheduled={isScheduled}
              scheduledTotal={scheduledTotal}
              budget={budget}
              amountInputRef={amountInputRef}
              onChangeBudgetAmount={onChangeBudgetAmount}
            />
          </HStack>

          {isScheduled ? (
            <GuidedBudgetScheduledPhasesSection
              scheduledSegments={scheduledSegments}
              eventTimezone={eventTimezone}
              deliveryWindowInEventTimezone={deliveryWindowInEventTimezone}
              scheduledBudgetDeliveryWindow={scheduledBudgetDeliveryWindow}
              scheduledBudgetEditPolicy={scheduledBudgetEditPolicy}
              phaseNeighborPickerBounds={phaseNeighborPickerBounds}
              scheduledPhaseTimelineError={scheduledPhaseTimelineError}
              scheduledBudgetFormErrors={scheduledBudgetFormErrors}
              budget={budget}
              onAddSegment={onAddSegment}
              onRemoveSegment={onRemoveSegment}
              onChangeSegment={onChangeSegment}
            />
          ) : null}
        </VStack>
      </Box>
    </VStack>
  );
}
